import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from 'pages/HomePublic/components/Header';
import { StepIndicator } from 'components/ui/StepIndicator';
import Participants from 'components/ui/Participants';
import ModalWarning from 'components/ui/ModalWarning';
import { ParticipationConfirmedModal } from 'components/ui/ParticipationConfirmedModal';
import { useModal } from 'context/ModalContext/Modal.context';
import { useAuth } from 'context/AuthContext';
import { useRule } from 'hooks/useRule';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { confirmarParticipacaoElbb } from 'services/iniciativa-elbb.service';
import { salvarIndicacaoGE, buscarIndicacaoExistente, buscarRankingParticipantes, buildRankingPontuado } from 'services/indicacao-ge.service';
import { getUserDetail } from 'services/auth.service';
import { registrarConfirmar } from 'services/iniciativa-action.service';
import { TeamData, Team } from 'types/realization';
import { HiUsers } from 'react-icons/hi';
import { FaBell } from "react-icons/fa6";
import { clearRegistrationUser } from 'utils/fns';

const steps = [
  { icon: HiUsers as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 1, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
  { icon: IoCheckmarkCircle as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 2, activeColor: 'bg-green-bb-2', iconColor: 'text-pink-bb-1' },
];

export function ConfirmacaoParticipacaoPremio() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const rule = useRule();
  const { showModal, closeModal } = useModal();

  const iniciativa = (location.state as any)?.iniciativa || null;
  const modoGE = (location.state as any)?.modoGE === true;

  const [currentStep, setCurrentStep] = useState(1);
  const [ruleUrl, setRuleUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<TeamData[]>([]);
  const [readonly, setReadonly] = useState(false);
  const [indicacaoAutor, setIndicacaoAutor] = useState<{ player: string; nome: string } | null>(null);
  const [rankingMap, setRankingMap] = useState<Map<string, { points: number; posicao: number }>>(new Map());

  useEffect(() => {
    if (!modoGE) {
      showModal(
        'participation-confirmed',
        <ParticipationConfirmedModal onClose={closeModal} />,
        'participation-confirmed-wrapper',
      );
    }
  }, [closeModal, showModal, modoGE]);

  useEffect(() => {
    if (rule) setRuleUrl(rule.url);
  }, [rule]);

  // Verifica indicacao existente e busca ranking no modoGE
  useEffect(() => {
    if (!modoGE || !id) return;

    async function verificarEstadoGE() {
      try {
        const [indicacao, ranking] = await Promise.all([
          buscarIndicacaoExistente(id!),
          buscarRankingParticipantes(id!),
        ]);

        setRankingMap(buildRankingPontuado(ranking, indicacao));

        if (indicacao) {
          setReadonly(true);
          const playerKey = indicacao.player || '';
          const detail = await getUserDetail(playerKey).catch(() => null);
          setIndicacaoAutor({
            player: playerKey,
            nome: detail?.nome_guerra || detail?.name || '',
          });
        }
      } catch (error) {
        console.error('Erro ao verificar estado GE:', error);
      }
    }

    verificarEstadoGE();
  }, [modoGE, id]);

  const teamData: TeamData[] = useMemo(() => {
    if (!iniciativa?.participantes) return [];
    const target = iniciativa.iniciativaOriginal?.target || [];
    const targetById = new Map<string, any>();
    target.forEach((t: any) => {
      const id = t.extra?.id || t._id?.replace(/^F/, '');
      if (id) targetById.set(id, t);
    });

    const participants = iniciativa.participantes.map((p: any) => {
      const t = targetById.get(p.matricula);

      return {
        _id: p.matricula,
        nome: t?.extra?.nome_guerra || p.nome,
        chave: `F${p.matricula}`,
        nome_guerra: t?.extra?.nome_guerra || p.nome.split(' ')[0],
        funcao: t?.extra?.funcao || '',
        prefixo: t?.extra?.prefixo || '',
        nome_dependencia: t?.extra?.nome_dependencia || '',
        searchField: `${p.matricula} F${p.matricula} ${p.nome} ${t?.extra?.nome_guerra || ''}`,
      } as TeamData;
    });

    // No modoGE, ordenar por ranking (mais votados primeiro)
    if (modoGE && rankingMap.size > 0) {
      participants.sort((a: TeamData, b: TeamData) => {
        const rankA = rankingMap.get(clearRegistrationUser(a._id))?.posicao ?? 999;
        const rankB = rankingMap.get(clearRegistrationUser(b._id))?.posicao ?? 999;
        return rankA - rankB;
      });
    }

    return participants;
  }, [iniciativa, rankingMap, modoGE]);

  const teamForParticipants: Team[] = useMemo(() => {
    const team = iniciativa?.iniciativaOriginal?.team;
    if (team) {
      return team.map((m: any) => ({
        user_id: m._id,
        message: m.message || '',
      }));
    }
    if (!iniciativa?.participantes) return [];
    return iniciativa.participantes.map((p: any) => ({
      user_id: p.matricula,
      message: '',
    }));
  }, [iniciativa]);

  const handlerSend = async () => {
    setIsSending(true);
    try {
      closeModal();
      if (!user || !id) return;

      const concorrentes_info = selectedParticipants.map((p) => ({
        player: p._id,
        nome: p.nome,
        chave: p.chave,
      }));

      if (modoGE) {
        await salvarIndicacaoGE(id, user._id, concorrentes_info);
        navigate('/minhas-iniciativas');
      } else {
        await confirmarParticipacaoElbb(id, user._id, concorrentes_info);

        try {
          await registrarConfirmar(user._id, id);
        } catch (actionLogError) {
          console.warn('Falha ao registrar action_log confirmar participação:', actionLogError);
        }

        navigate('/minhas-iniciativas');
      }
    } catch (error: any) {
      console.error(modoGE ? 'Erro ao salvar votação GE:' : 'Erro ao confirmar participação:', error);
      if (modoGE && error?.message === 'Votação já realizada para esta iniciativa.') {
        setReadonly(true);
        let autor = indicacaoAutor;
        if (!autor) {
          const indicacao = await buscarIndicacaoExistente(id!).catch(() => null);
          if (indicacao) {
            const playerKey = indicacao.player || '';
            const detail = await getUserDetail(playerKey).catch(() => null);
            autor = { player: playerKey, nome: detail?.nome_guerra || detail?.name || '' };
            setIndicacaoAutor(autor);
          }
        }
        const autorLabel = autor
          ? `${autor.player}${autor.nome ? ` — ${autor.nome}` : ''}`
          : 'outro Gestor Executivo';
        showModal(
          'indicacao-ja-realizada',
          <ModalWarning
            title="Votação já realizada"
            description={`Outro Gestor Executivo já realizou a votação dos participantes para esta iniciativa. Responsável: ${autorLabel}.`}
            btnText="Entendi"
            onClose={closeModal}
          />,
          'indicacao-ja-realizada',
        );
      }
    } finally {
      setIsSending(false);
    }
  };

  const showTermsModal = () => {
    showModal(
      'confirm-participation',
      <ModalWarning
        rule={ruleUrl}
        title="Confirmar participação"
        subTitle="Confirme abaixo algumas informações importantes antes de finalizar."
        needInfo={true}
        needRule={true}
        description={
          <div className="flex flex-col gap-3">
            <p>Declaro que li e concordo com os termos estabelecidos na 403-1, item 7.2.</p>
            <p>
              Estou ciente sobre os prazos de inscrição, requisitos de participação, critérios de avaliação e os meus direitos e deveres como participante do Prêmio BB.
            </p>
            <p>Comprometo-me a fornecer informações precisas e completas, além de seguir todas as regras e regulamentos da premiação.</p>
            <p>Autorizo a organização a usar minha imagem em fotos ou vídeos, bem como meus dados pessoais, para fins de divulgação.</p>
            <p>
              Tenho conhecimento de que será realizada a verificação dos requisitos de impedimentos por alguma das situações listadas na IN 403-1, item 7.2.8.2, com base na situação apurada no primeiro dia útil após o encerramento das inscrições.
            </p>
          </div>
        }
        btnText="Confirmar participação"
        hasBackButton={true}
        onBackButton={closeModal}
        onClose={handlerSend}
      />,
      'confirm-participation'
    );
  };

  const handleProximaEtapa = () => {
    if (modoGE) {
      // GE nao precisa aceitar termos, salva direto
      handlerSend();
    } else {
      showTermsModal();
    }
  };

  const handleVoltar = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  const isNextDisabled = selectedParticipants.length < 5 || isSending || readonly;

  const titulo = modoGE
    ? 'Abaixo, indique os 5 participantes que mais atuaram para o sucesso da entrega desta iniciativa.'
    : 'Abaixo, indique os 5 participantes que mais atuaram para o sucesso da entrega desta iniciativa. Caso a iniciativa seja premiada na última etapa de avaliação, os 5 poderão ser contemplados com o prêmio individual.';

  return (
    <div className="min-h-screen bg-gray-100">
      <Header currentPage={modoGE ? "minhas-iniciativas" : "iniciativas"} />

      {!modoGE && (
        <div className="py-6">
          <div className="max-w-[1200px] mx-auto px-4">
            <StepIndicator currentStep={currentStep} steps={steps} progressColor="bg-blue-bb-6" />
          </div>
        </div>
      )}

      <main className="px-4 py-8 pb-16">
        <div className="max-w-[1200px] mx-auto">

          {readonly && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
              <p className="text-base text-blue-800 font-semibold">
                A indicação desta iniciativa já foi realizada.
                {indicacaoAutor && (
                  <span className="font-normal">
                    {' '}Responsável: {indicacaoAutor.player}{indicacaoAutor.nome ? ` — ${indicacaoAutor.nome}` : ''}.
                  </span>
                )}
              </p>
            </div>
          )}

            <h2 className="text-2xl md:text-[32px] font-bold text-gray-bb-1 mb-6 font-title leading-tight">
              {titulo}
            </h2>

          {!modoGE && (
           <div className="flex items-center gap-4 border border-pink-bb-2 rounded-xl p-4 mb-8 relative overflow-hidden">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
              <FaBell className="text-pink-bb w-20 h-20 absolute -left-6" />
            </div>
            <p className="text-base text-gray-600 leading-relaxed">
              Durante o período de inscrição, os funcionários que estiverem em "Situação Diferente de 100 – Força de Trabalho Real" devem confirmar a participação no Prêmio e a adesão ao Regulamento da premiação entrando em contato com a Gepes Atendimento pelos telefones: 4003-5291 (Capitais e regiões metropolitanas) ou 0800 881 5291 (demais localidades).
            </p>
          </div>
          )}

          <Participants
            options={teamData}
            limitSelected={5}
            minSelected={5}
            onSelected={(val) => setSelectedParticipants(val || [])}
            onBack={handleVoltar}
            onNext={handleProximaEtapa}
            isNextDisabled={isNextDisabled}
            team={teamForParticipants}
            nextButtonText={modoGE ? "FINALIZAR INDICAÇÃO" : "Finalizar Inscrição"}
            rankingMap={modoGE ? rankingMap : undefined}
          />
        </div>
      </main>
    </div>
  );
}
